'use client'
import Image from 'next/image'
import { Button } from '@repo/design-system-cf/components/button'
import { IconAdd, IconPhotoPrints } from '@repo/design-system-cf/components/icons'
import { tw } from 'utils/classnames'
import { SkeletonItem } from 'components/skeleton-item'
import { useSocialLatestDownloadImagesQuery } from '@web/gen/crafter'
import { useCreatePostProjectDialog } from '../create-post-project-dialog/create-post-project-dialog-context'

export function PostProjectSection() {
  const { openDialog } = useCreatePostProjectDialog()
  const { data, isPending } = useSocialLatestDownloadImagesQuery(undefined, {
    select: data => data.socialLatestDownloadImages?.images || [],
  })

  const images = data || []

  if (isPending) {
    return <PostProjectSectionSkeleton />
  }

  return (
    <div
      className={tw(
        'rounded-[12px] relative bg-cf-purple-50 p-2 pb-4 border-[#1313151F] border mb-4',
        images.length === 0 ? '' : 'mt-4',
      )}
    >
      {images.length > 0 ? (
        <section
          className={tw(
            'relative w-[240px] h-[143px] overflow-hidden ',
            images.length === 0 ? '' : '-mt-8',
            images.length === 1 ? 'h-[106px]' : '',
            images.length === 2 ? 'h-[126px]' : '',
          )}
        >
          <div className="relative w-full h-full">
            <Image
              src={images[0] || ''}
              data-testid="post-project-section-image-1"
              alt={'Feature image 1'}
              width={190}
              height={120}
              className={tw(
                'absolute top-3 left-6 rounded-[12px] object-cover object-top shadow-md -rotate-6 w-[190px] border-[#1313151F] border bg-cf-white',
                images.length === 1 ? 'left-1/2 -translate-x-1/2' : '',
              )}
            />
            {images[1] ? (
              <Image
                src={images[1] || ''}
                data-testid="post-project-section-image-2"
                alt={'Feature image 2'}
                width={190}
                height={120}
                className="absolute top-7 right-6 rounded-[12px] object-cover object-top shadow-lg rotate-3 w-[190px] border-[#1313151F] border bg-cf-white"
              />
            ) : null}
            {images[2] ? (
              <Image
                src={images[2] || ''}
                data-testid="post-project-section-image-3"
                alt={'Feature image 3'}
                width={190}
                height={120}
                className="absolute top-16 left-4 rounded-[12px] object-cover object-top shadow-xl -rotate-[4deg] w-[190px] border-[#1313151F] border bg-cf-white"
              />
            ) : null}
          </div>
        </section>
      ) : (
        <div className="pt-2 pl-2">
          <IconPhotoPrints className="size-10 text-cf-purple-900" />
        </div>
      )}
      <div className="px-2 pt-3">
        <p className="text-16 font-semibold text-cf-grey-1000 mb-2">Share your latest project!</p>
        <p className="text-14 text-cf-grey-1000 mb-4">
          Whether you used these recent products or tackled something completely different, share
          your work and inspire others.
        </p>
        <Button iconLeft={<IconAdd />} className="w-full" onClick={openDialog}>
          Add a new project
        </Button>
      </div>
    </div>
  )
}

export function PostProjectSectionSkeleton() {
  return (
    <div className="rounded-[12px] relative bg-cf-purple-50 p-2 pb-4 border-[#1313151F] border mb-4 mt-4">
      {/* images skeleton */}
      <section className="relative w-[240px] h-[143px] overflow-hidden -mt-8">
        <div className="relative w-full h-full">
          <SkeletonItem
            className={tw(
              'absolute top-3 left-6 w-[190px] h-[120px] rounded-[12px] border border-[#1313151F]',
              '-rotate-6',
            )}
          />
          <SkeletonItem
            className={tw(
              'absolute top-7 right-6 w-[190px] h-[120px] rounded-[12px] border border-[#1313151F]',
              'rotate-3',
            )}
          />
          <SkeletonItem
            className={tw(
              'absolute top-16 left-4 w-[190px] h-[120px] rounded-[12px] border border-[#1313151F]',
              '-rotate-[4deg]',
            )}
          />
        </div>
      </section>

      {/* text + button skeleton */}
      <div className="px-2 pt-3">
        <SkeletonItem className="h-[18px] w-[70%] mb-2 rounded-[6px]" />
        <SkeletonItem className="h-[14px] w-[95%] mb-1 rounded-[6px]" />
        <SkeletonItem className="h-[14px] w-[80%] mb-4 rounded-[6px]" />
        <SkeletonItem className="h-10 w-full rounded-[8px]" />
      </div>
    </div>
  )
}
